import { useEffect, useRef, useState } from 'react';

import { apiV1Base } from '../lib/apiConfig';
type Allocation = {
  account_id: string;
  data_status: 'live' | 'empty' | 'unavailable' | 'error';
  buckets: Array<{ name: string; value: number; weight_pct: number; color: string }>;
  total_value?: number;
  sector_coverage_pct?: number;
  grouping?: string;
  provider?: string;
  last_updated?: string;
  message?: string;
};

type Health = {
  account_id: string;
  data_status: 'live' | 'unavailable' | 'error';
  score: number | null;
  status: 'healthy' | 'watch' | 'caution' | 'critical' | 'unavailable';
  reasons: Array<{ level: 'critical' | 'high' | 'medium' | 'info'; message: string }>;
  inputs?: Record<string, number>;
  last_updated?: string;
  provider?: string;
  message?: string;
};

type RiskAlerts = {
  account_id: string;
  data_status: 'live' | 'unavailable' | 'error';
  alerts: Array<{
    id: string;
    type: 'high' | 'warning' | 'safe' | 'info';
    title: string;
    message: string;
    symbol?: string;
    rule?: string;
  }>;
  last_updated?: string;
  provider?: string;
  message?: string;
};

const POLL_MS = 30_000;

function apiBase(): string {
  if (typeof window === 'undefined') return '';
  const host = window.location.hostname;
  return apiV1Base();
}

async function fetchEnvelope<T>(path: string, signal: AbortSignal): Promise<T | null> {
  try {
    const res = await fetch(`${apiBase()}${path}`, { signal, cache: 'no-store' });
    if (!res.ok) return null;
    const body = await res.json();
    if (!body?.ok) return null;
    return body.data as T;
  } catch {
    return null;
  }
}

export function useAccountIntel(accountId: string | null | undefined) {
  const [allocation, setAllocation] = useState<Allocation | null>(null);
  const [health, setHealth] = useState<Health | null>(null);
  const [riskAlerts, setRiskAlerts] = useState<RiskAlerts | null>(null);
  const [loading, setLoading] = useState(true);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    if (!accountId) {
      setAllocation(null);
      setHealth(null);
      setRiskAlerts(null);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    const fetchOnce = async () => {
      const [a, h, r] = await Promise.all([
        fetchEnvelope<Allocation>(`/portfolio/${encodeURIComponent(accountId)}/allocation`, controller.signal),
        fetchEnvelope<Health>(`/portfolio/${encodeURIComponent(accountId)}/health`, controller.signal),
        fetchEnvelope<RiskAlerts>(`/portfolio/${encodeURIComponent(accountId)}/risk-alerts`, controller.signal),
      ]);
      if (!mounted.current) return;
      setAllocation(a);
      setHealth(h);
      setRiskAlerts(r);
      setLoading(false);
    };
    fetchOnce();
    const id = setInterval(() => {
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') {
        fetchOnce();
      }
    }, POLL_MS);
    return () => {
      mounted.current = false;
      controller.abort();
      clearInterval(id);
    };
  }, [accountId]);

  return { allocation, health, riskAlerts, loading };
}

type MarketsOverview = {
  tiles: Array<{
    symbol: string;
    display_symbol: string;
    display_label?: string;
    asset_class: string;
    value: number | null;
    change_pct: number | null;
    spark: Array<{ value: number }>;
    data_status: 'live' | 'unavailable' | 'error';
    message?: string;
    provider: string;
  }>;
  session: string;
  data_status: string;
  provider: string;
  last_updated: string;
};

export function useMarketsOverview() {
  const [overview, setOverview] = useState<MarketsOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    const controller = new AbortController();
    const fetchOnce = async () => {
      const data = await fetchEnvelope<MarketsOverview>(`/markets/overview`, controller.signal);
      if (!mounted.current) return;
      setOverview(data);
      setLoading(false);
    };
    fetchOnce();
    const id = setInterval(() => {
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') {
        fetchOnce();
      }
    }, POLL_MS);
    return () => {
      mounted.current = false;
      controller.abort();
      clearInterval(id);
    };
  }, []);

  return { overview, loading };
}

export type Mover = { symbol: string; price: number; change_pct: number; volume?: number };
type MoversResponse = {
  movers: Mover[];
  data_status: 'live' | 'unavailable' | 'error' | 'not-implemented';
  message?: string;
  provider: string;
  last_updated: string;
};

export function useMarketMovers() {
  const [data, setData] = useState<MoversResponse | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const controller = new AbortController();
    let alive = true;
    (async () => {
      const d = await fetchEnvelope<MoversResponse>(`/markets/movers`, controller.signal);
      if (!alive) return;
      setData(d);
      setLoading(false);
    })();
    return () => {
      alive = false;
      controller.abort();
    };
  }, []);
  return { data, loading };
}

export default useAccountIntel;
